import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import { useQwanta, QwantaContext, persist } from "qwanyx";
import { IconButton } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { TableSettings } from './TableSettings';

// Translation system
const translations = {
    english: {
        total: "Total",
        tax: "VAT",
        totalIncludingTax: "Total Including Tax",
        designation: "Designation",
        quantity: "Quantity",
        price: "Price",
        amount: "Amount"
    },
    french: {
        total: "Total",
        tax: "TVA",
        totalIncludingTax: "Total TTC",
        designation: "Désignation",
        quantity: "Quantité",
        price: "Prix",
        amount: "Montant"
    }
};

export const Table = ({
    name = "Table",
    qwantumId,
    onSave,
    columns = [
        { title: "Column 1", width: "33%", defaultValue: "", editable: true },
        { title: "Column 2", width: "33%", defaultValue: "", editable: true },
        { title: "Column 3", width: "34%", defaultValue: "", editable: true }
    ],
    rows = [
        ["Value 1", "Value 2", "Value 3"],
        ["Value 4", "Value 5", "Value 6"]
    ],
    width = "100%",
    height = "auto",
    striped = false,
    bordered = false,
    hoverable = false,
    invoiceMode = false,
    currency = "$",
    vatRate = 20,
    language = "english",
    allowRowManagement = true,
    style = {},
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
        actions: { setProp }
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // Qwanta persistence
    const [persistedRows, setPersistedRows] = useQwanta(qwantumId);
    const qwantaContext = useContext(QwantaContext);
    const hasLoadedRef = useRef(false);

    const [localRows, setLocalRows] = useState(rows);
    const [editingCell, setEditingCell] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [prevValue, setPrevValue] = useState(JSON.stringify(rows));

    // Load persisted data when available (once only)
    useEffect(() => {
        if (qwantumId && persistedRows && Array.isArray(persistedRows) && !hasLoadedRef.current) {
            setLocalRows(persistedRows);
            setProp(props => props.rows = persistedRows);
            hasLoadedRef.current = true;
        }
    }, [persistedRows, qwantumId, setProp]);

    // Sync localRows with props.rows when it changes (from settings updates)
    useEffect(() => {
        if (!hasLoadedRef.current || !persistedRows) {
            setLocalRows(rows);
        }
    }, [rows, persistedRows]);

    // Also sync when columns change to ensure data structure matches
    useEffect(() => {
        if (!hasLoadedRef.current || !persistedRows) {
            setLocalRows(rows);
        }
    }, [columns, persistedRows]);

    // Update prevValue when localRows changes
    useEffect(() => {
        setPrevValue(JSON.stringify(localRows));
    }, [localRows]);

    const t = translations[language] || translations.english;

    // Set up invoice mode columns when enabled
    useEffect(() => {
        if (invoiceMode) {
            const invoiceColumns = [
                { title: t.designation, width: "70%", defaultValue: "", editable: true },
                { title: t.quantity, width: "10%", defaultValue: "1", editable: true },
                { title: t.price, width: "10%", defaultValue: "0.00", editable: true },
                { title: t.amount, width: "10%", defaultValue: "0.00", editable: false }
            ];
            const invoiceRows = [
                ["Product/Service", "1", "100.00", "100.00"],
                ["Another item", "2", "50.00", "100.00"]
            ];
            
            setProp(props => {
                props.columns = invoiceColumns;
                props.rows = invoiceRows;
            });
        }
    }, [invoiceMode, language, setProp, t]);

    // Invoice calculations
    const formatCurrency = (amount) => {
        const num = parseFloat(amount) || 0;
        return `${currency}${num.toFixed(2)}`;
    };

    const calculateAmount = (quantity, price) => {
        const qty = parseFloat(quantity) || 0;
        const prc = parseFloat(price) || 0;
        return (qty * prc).toFixed(2);
    };

    const calculateInvoiceTotals = () => {
        if (!invoiceMode) return { total: 0, vat: 0, ttc: 0 };

        const total = localRows.reduce((sum, row) => {
            const amount = parseFloat(row[3]) || 0;
            return sum + amount;
        }, 0);

        const vat = (total * vatRate) / 100;
        const ttc = total + vat;

        return { total, vat, ttc };
    };

    const { total, vat, ttc } = calculateInvoiceTotals();

    const getSelectionStyle = () => {
        if (!enabled) return {};
        if (isSelected) {
            return { outline: '2px dashed #2196f3', outlineOffset: '2px' };
        }
        if (isHovered) {
            return { outline: '1px dashed #ff9800', outlineOffset: '1px' };
        }
        return {};
    };

    const getTableClasses = () => {
        let classes = ["table"];
        if (striped) classes.push("is-striped");
        if (hoverable) classes.push("is-hoverable");
        if (bordered) classes.push("is-bordered");
        return classes.join(" ");
    };

    const addRow = () => {
        const newRow = invoiceMode 
            ? ["New item", "1", "0.00", "0.00"]
            : columns.map(col => col.defaultValue || "");
        const updatedRows = [...localRows, newRow];
        setLocalRows(updatedRows);
        setProp(props => props.rows = updatedRows);
        
        // Persist the change
        if (qwantumId && qwantaContext) {
            const delta = { [qwantumId]: updatedRows };
            persist(delta, qwantaContext._id, qwantaContext.di, qwantaContext.ed, false);
        }
    };

    const removeRow = (index) => {
        if (localRows.length > 0) {
            const updatedRows = localRows.filter((_, i) => i !== index);
            setLocalRows(updatedRows);
            setProp(props => props.rows = updatedRows);
            
            // Persist the change
            if (qwantumId && qwantaContext) {
                const delta = { [qwantumId]: updatedRows };
                persist(delta, qwantaContext._id, qwantaContext.di, qwantaContext.ed, false);
            }
        }
    };

    const handleCellClick = (rowIndex, colIndex) => {
        if (invoiceMode && colIndex === 3) return; // Amount column not editable in invoice mode
        
        if (effectiveColumns[colIndex]?.editable) {
            setEditingCell({ row: rowIndex, col: colIndex });
            setEditingValue(localRows[rowIndex][colIndex]);
        }
    };

    const handleCellChange = (e) => {
        const newValue = e.target.value;
        setEditingValue(newValue);
        
        if (editingCell) {
            const updatedRows = localRows.map((row, rIdx) => {
                if (rIdx === editingCell.row) {
                    const newRow = [...row];
                    newRow[editingCell.col] = newValue;
                    
                    // Auto-calculate Amount in invoice mode
                    if (invoiceMode && (editingCell.col === 1 || editingCell.col === 2)) {
                        const quantity = editingCell.col === 1 ? newValue : newRow[1];
                        const price = editingCell.col === 2 ? newValue : newRow[2];
                        newRow[3] = calculateAmount(quantity, price);
                    }
                    
                    return newRow;
                }
                return row;
            });
            setLocalRows(updatedRows);
        }
    };

    const handleCellBlur = () => {
        if (editingCell) {
            setProp(props => props.rows = localRows);
        }
        setEditingCell(null);
        setEditingValue('');
        
        // Persist data after editing
        if (qwantumId && qwantaContext) {
            const delta = { [qwantumId]: localRows };
            persist(delta, qwantaContext._id, qwantaContext.di, qwantaContext.ed, false);
        }
    };

    const handleCellKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCellBlur();
        }
    };

    const containerStyle = {
        width,
        height,
        overflow: 'auto',
        position: 'relative',
        ...style,
        ...getSelectionStyle()
    };

    const effectiveColumns = invoiceMode ? [
        { title: t.designation, width: "70%", defaultValue: "", editable: true },
        { title: t.quantity, width: "10%", defaultValue: "1", editable: true },
        { title: t.price, width: "10%", defaultValue: "0.00", editable: true },
        { title: t.amount, width: "10%", defaultValue: "0.00", editable: false }
    ] : columns;

    return (
        <div
            ref={ref => {
                if (enabled) {
                    connect(drag(ref));
                } else {
                    connect(ref);
                }
            }}
            name={name}
            style={containerStyle}
            className="table-container"
            tabIndex={0}
            {...props}
        >
            <style>
                {`
                .table {
                    width: 100%;
                    border-collapse: collapse;
                    font-family: Arial, sans-serif;
                    table-layout: auto;
                }
                .table.is-striped tbody tr:nth-child(odd) {
                    background-color: #f8f9f9 !important;
                }
                .table.is-striped tbody tr:nth-child(even) {
                    background-color: #ffffff !important;
                }
                .table.is-hoverable tbody tr:hover {
                    background-color: #f5f5f5;
                }
                .table.is-bordered th,
                .table.is-bordered td {
                    border: 1px solid #dbdbdb;
                }
                .table th {
                    background-color: #f8f9fa;
                    font-weight: 600;
                    padding: 12px 8px;
                    text-align: left;
                    border-bottom: 2px solid #dee2e6;
                }
                .table td {
                    padding: 8px;
                    border-bottom: 1px solid #dee2e6;
                }
                .table-actions {
                    width: 60px;
                    text-align: center;
                }
                @media print {
                    .table-actions {
                        display: none !important;
                    }
                }
                `}
            </style>
            
            <table className={getTableClasses()}>
                <colgroup>
                    {effectiveColumns.map((column, index) => (
                        <col key={index} style={{ width: column.width }} />
                    ))}
                    {allowRowManagement && <col style={{ width: '60px' }} />}
                </colgroup>
                
                <thead>
                    <tr>
                        {effectiveColumns.map((column, index) => (
                            <th key={index}>
                                {column.title}
                            </th>
                        ))}
                        {allowRowManagement && (
                            <th className="table-actions">
                                <IconButton
                                    onClick={addRow}
                                    size="small"
                                    title="Add Row"
                                >
                                    <AddIcon />
                                </IconButton>
                            </th>
                        )}
                    </tr>
                </thead>
                
                <tbody>
                    {localRows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {row.map((cell, colIndex) => (
                                <td
                                    key={colIndex}
                                    onClick={() => handleCellClick(rowIndex, colIndex)}
                                    style={{
                                        cursor: effectiveColumns[colIndex]?.editable ? 'pointer' : 'default',
                                        backgroundColor: !effectiveColumns[colIndex]?.editable ? '#f8f9fa' : 'inherit'
                                    }}
                                >
                                    {editingCell && editingCell.row === rowIndex && editingCell.col === colIndex ? (
                                        <input
                                            type="text"
                                            value={editingValue}
                                            onChange={handleCellChange}
                                            onBlur={handleCellBlur}
                                            onKeyDown={handleCellKeyDown}
                                            autoFocus
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                width: '100%',
                                                outline: 'none'
                                            }}
                                        />
                                    ) : (
                                        <span>
                                            {invoiceMode && (colIndex === 2 || colIndex === 3) ?
                                                formatCurrency(cell) : cell}
                                        </span>
                                    )}
                                </td>
                            ))}
                            {allowRowManagement && (
                                <td className="table-actions">
                                    <IconButton
                                        onClick={() => removeRow(rowIndex)}
                                        disabled={localRows.length === 0}
                                        size="small"
                                        title="Remove Row"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
                
                {invoiceMode && (
                    <tfoot>
                        <tr>
                            <td colSpan={allowRowManagement ? "5" : "4"} style={{
                                borderTop: '2px solid #ddd',
                                height: '0px',
                                padding: '0px'
                            }}></td>
                        </tr>
                        <tr style={{ border: 'none' }}>
                            <th colSpan={allowRowManagement ? "3" : "3"} style={{
                                textAlign: 'right',
                                border: 'none',
                                padding: '8px'
                            }}>{t.total}:</th>
                            <th style={{
                                textAlign: 'right',
                                border: 'none',
                                padding: '8px'
                            }}>{formatCurrency(total)}</th>
                            {allowRowManagement && <th style={{ border: 'none' }}></th>}
                        </tr>
                        <tr style={{ border: 'none' }}>
                            <th colSpan={allowRowManagement ? "3" : "3"} style={{
                                textAlign: 'right',
                                border: 'none',
                                padding: '8px'
                            }}>{t.tax} ({vatRate}%):</th>
                            <th style={{
                                textAlign: 'right',
                                border: 'none',
                                padding: '8px'
                            }}>{formatCurrency(vat)}</th>
                            {allowRowManagement && <th style={{ border: 'none' }}></th>}
                        </tr>
                        <tr style={{ border: 'none' }}>
                            <th colSpan={allowRowManagement ? "3" : "3"} style={{
                                textAlign: 'right',
                                fontWeight: 'bold',
                                border: 'none',
                                padding: '8px',
                                borderTop: '1px solid #000'
                            }}>{t.totalIncludingTax}:</th>
                            <th style={{
                                textAlign: 'right',
                                fontWeight: 'bold',
                                border: 'none',
                                padding: '8px',
                                borderTop: '1px solid #000'
                            }}>{formatCurrency(ttc)}</th>
                            {allowRowManagement && <th style={{ border: 'none', borderTop: '1px solid #000' }}></th>}
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

Table.craft = {
    props: {
        name: "Table",
        qwantumId: "",
        columns: [
            { title: "Column 1", width: "33%", defaultValue: "", editable: true },
            { title: "Column 2", width: "33%", defaultValue: "", editable: true },
            { title: "Column 3", width: "34%", defaultValue: "", editable: true }
        ],
        rows: [
            ["Value 1", "Value 2", "Value 3"],
            ["Value 4", "Value 5", "Value 6"]
        ],
        width: "100%",
        height: "auto",
        striped: false,
        bordered: false,
        hoverable: false,
        invoiceMode: false,
        currency: "$",
        vatRate: 20,
        language: "english",
        allowRowManagement: true
    },
    related: {
        settings: TableSettings
    },
    displayName: "Table"
};